import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import WhatsAppBubble from "@/components/WhatsAppBubble";
import BackToTopButton from "@/components/BackToTopButton";

// Only the homepage (Index) is eagerly bundled — it's the most-trafficked page and
// the critical path for first paint. Every other route is lazy-loaded so it doesn't
// bloat the homepage bundle (smaller JS => faster LCP/TTI => higher Lighthouse score).
// SmallBusiness pulls in framer-motion and LeadForm is a large multi-step form, so
// keeping them out of the initial chunk is a big win.
const SmallBusiness = lazy(() => import("./pages/SmallBusiness"));
const LeadForm = lazy(() => import("./pages/LeadForm"));
const Login = lazy(() => import("./pages/Login"));
const AuthConfirm = lazy(() => import("./pages/AuthConfirm"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Portal = lazy(() => import("./pages/Portal"));
const LeadsList = lazy(() => import("./pages/LeadsList"));
const LeadDetail = lazy(() => import("./pages/LeadDetail"));
const UsersManagement = lazy(() => import("./pages/UsersManagement"));
const PromoCodesManagement = lazy(() => import("./pages/PromoCodesManagement"));

const queryClient = new QueryClient();

// Global safety net for unhandled promise rejections (prevents Safari white screens)
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    event.preventDefault();
  });
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ErrorBoundary>
        <AuthProvider>
          <Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(135deg, hsl(220 25% 8%) 0%, hsl(220 20% 14%) 100%)' }} />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/websites" element={<SmallBusiness />} />
            <Route path="/get-started" element={<LeadForm />} />
            {/* Auth and Portal routes (lazy-loaded — not in the public bundle) */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/confirm" element={<AuthConfirm />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/portal" element={<Portal />} />
            <Route path="/portal/leads" element={<LeadsList />} />
            <Route path="/portal/leads/:id" element={<LeadDetail />} />
            <Route path="/portal/users" element={<UsersManagement />} />
            <Route path="/portal/promo-codes" element={<PromoCodesManagement />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AuthProvider>
        <BackToTopButton />
        <WhatsAppBubble />
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
